import { Link } from "react-router-dom";
import { Inbox, Loader2 } from "lucide-react";

import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { DocumentBreadcrumbHover } from "@/domains/pm/components/issues/DocumentBreadcrumbHover";
import { useDashboardDocumentOpener } from "@/domains/documents/hooks/useDashboardDocumentOpener";
import { useUploadedToday } from "@/domains/documents/hooks/useUploadedToday";
import { isDocumentProcessing } from "@/domains/documents/utils/documentState";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface UploadedTodayWidgetProps {
  buildingId: string;
  /** The active project (`building_projects.id`), or null at "All projects"
   * scope — the feed then covers the whole building. */
  projectId: number | null;
}

/** Rows shown inline; "View all →" opens the documents list for the rest. */
const VISIBLE_ROWS = 5;

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * "Uploaded today" — the right-hand column of the Document analytics second
 * row, beside Upload activity: today's uploads newest first as tinted
 * file-type chip · filename · upload time. Previewable rows open the shared
 * document viewer; rows whose file is still processing (e.g. an unconverted
 * DWG) carry an inline spinner instead of the click affordance. Hovering a row
 * shows the interactive project → folder → filename breadcrumb (same hover
 * card as PM linked docs / message attachments).
 */
export function UploadedTodayWidget({ buildingId, projectId }: UploadedTodayWidgetProps) {
  const { t } = useLanguage();
  const { data: documents = [], isLoading } = useUploadedToday(buildingId, projectId);
  const opener = useDashboardDocumentOpener(buildingId);

  const tw = t.buildings.projectsDashboard.uploadedToday;
  const visible = documents.slice(0, VISIBLE_ROWS);

  return (
    // h-full: fill the grid row beside Upload activity, so an empty feed can
    // centre its icon against the chart's height.
    <div className="flex h-full min-w-0 flex-col">
      {/* min-h-7 matches ChartTitleRow's expand button, so this title sits on
          the same line as the Upload activity title beside it. */}
      <div className="flex min-h-7 items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-sm font-medium text-neutral-700">{tw.title}</h3>
          {documents.length > 0 && (
            <span className="flex-shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground">
              {documents.length}
            </span>
          )}
        </span>
        {documents.length > 0 && (
          <Link
            to={`/buildings/${buildingId}/documents`}
            className="flex-shrink-0 text-xs font-medium text-primary hover:underline"
          >
            {tw.viewAll} →
          </Link>
        )}
      </div>

      {documents.length === 0 && !isLoading ? (
        // Same icon-over-title empty state as the Upload activity chart beside
        // it; min-h matches that chart's plot height when the columns stack.
        <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-1 pt-3 text-center">
          <Inbox className="mb-1 h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm font-medium text-neutral-700">{tw.empty}</p>
        </div>
      ) : (
        // -mx-2 lets a row's hover pill bleed slightly past the column edge
        // while the row content stays flush with the heading.
        <ul className="-mx-2 space-y-0.5 pt-2">
          {visible.map((doc) => {
            const previewable = opener.canOpen(doc);
            // Only the rows you can't open yet get the spinner: a converted DWG
            // sits in DWG_PROCESSED (not a terminal state) but is openable, and
            // a complete non-previewable type (.zip) isn't processing at all.
            const processing = !previewable && isDocumentProcessing(doc.state);
            const row = (
              <>
                <FileTypeIcon filename={doc.filename} className="h-6 w-6" />
                <span className="min-w-0 flex-1 truncate text-sm">{doc.filename}</span>
                {processing && (
                  <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-blue-500" />
                )}
                <span className="flex-shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatTime(doc.created_at)}
                </span>
              </>
            );
            return (
              <li key={doc.id}>
                <DocumentBreadcrumbHover
                  segments={doc.breadcrumbSegments}
                  buildingId={Number(buildingId)}
                >
                  {previewable ? (
                    <button
                      type="button"
                      onClick={() => opener.open(doc)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-[#efeff1]"
                    >
                      {row}
                    </button>
                  ) : (
                    <div className="flex w-full items-center gap-2 rounded-md px-2 py-1.5">
                      {row}
                    </div>
                  )}
                </DocumentBreadcrumbHover>
              </li>
            );
          })}
        </ul>
      )}

      {opener.dialog}
    </div>
  );
}
